import React from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
//#region redux
import { useDispatch, useSelector } from "react-redux";
import { ThunkDispatch } from "redux-thunk";
import { AppState } from "store";
//#endregion
//#region types
import { EntityIdParams } from "models/propTypes/urlParamsTypes";
import { TimeReportingFormTypes, TimeReportingProjectFormTypes } from "models/formTypes";
import { WintSuperkollAPIModel, WintSuperkollAPIModelTimeReporting as ApiModel } from "wint-api-client/dist/types";
import { TranslationKeys } from "translations";
import { DialogEnums } from "models/enums";
import { IconDefinitionConstants } from "utils/constants";
import { DialogProps } from "models/propTypes/dialogPropTypes";
//#endregion
//#region components
import {
  PageLayouts,
  RouteBreadCrumb,
  SearchContextProvider,
  useMountedState,
  ReloadEvent,
  LoadingWithLogo,
} from "components/common";
import { closeDialog, displayDialog, displayErrorMessage, displaySuccessMessage } from "store/actions/utilActions";
import CreateProfileButtons from "./createProfileButtons";
import TimeReportingProjectListViewContainer from "./timeReportingProjectListViewContainer";
import useTimeReportingProjectProfileFilterDefinition from "./forms/useTimeReportingProjectProfileFilterDefinition";
import useSendInvoiceDocumentationQueryDialog from "./useSendInvoiceDocumentationQueryDialog";
import ConnectInvoiceFormContainer from "./connectInvoice/connectInvoiceFormContainer";
import { useTimeReportingProjectService } from "serviceHooks";
import { useSalaryPermissions } from "components/salary";
//#endregion
import { LocalizationHelperInstance, UrlHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const TimeReportingProjectProfile: React.FC = () => {
  const timeReportingProjectService = useTimeReportingProjectService();
  const urlParams = useParams<EntityIdParams>();
  const id = urlParams.id!;
  const { getMounted } = useMountedState();
  const [project, setProject] = React.useState<ApiModel.TimeReportingProject>();
  const [projectHasUnInvoicedProjectTime, setProjectHasUnInvoicedProjectTime] = React.useState<boolean>(false);
  const [invoiceDocumentation, setInvoiceDocumentation] = React.useState<TimeReportingFormTypes.InvoiceDocumentation>();
  const [createInvoiceDraftsButtonSubmitting, setCreateInvoiceDraftsButtonSubmitting] = React.useState<boolean>(false);

  const dispatch: ThunkDispatch<any, any, any> = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = React.useState<boolean>(true);
  const { filterFormFields, filterQueryStateHandler } = useTimeReportingProjectProfileFilterDefinition(
    id,
    project?.startDate
  );
  const getSendInvoiceDocumentationQueryDialog = useSendInvoiceDocumentationQueryDialog();
  const { isSalaryDeviationsAvailable } = useSalaryPermissions();

  const getProject = () => {
    timeReportingProjectService
      .getTimeReportingProject(id)
      .then((result) => {
        if (getMounted()) {
          setProject(result);
          timeReportingProjectService.hasUnInvoicedProjectTime(id).then((res) => {
            setProjectHasUnInvoicedProjectTime(res);
          });
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  React.useEffect(() => {
    setLoading(true);
    loadData();
  }, [id]);

  const loadData = () => {
    getProject();
  };

  const createInvoiceDraftsButtonEnabled = React.useMemo(() => {
    return !!invoiceDocumentation?.data;
  }, [invoiceDocumentation]);

  const [sellerDimensionType] = useSelector((state: AppState) => [
    state.common.dimensions?.invoiceDimensions?.find(
      (d) => d.key.type === WintSuperkollAPIModel.DimensionCompatibilityType.Seller
    ),
  ]);

  const processInvoiceDrafts = async () => {
    if (
      !invoiceDocumentation?.data?.personCategoryTimes ||
      invoiceDocumentation.data.personCategoryTimes?.length === 0
    ) {
      displayNoInvoiceDraftsCreated();
      return;
    }

    if (invoiceDocumentation.hasUnattestedRecords && isSalaryDeviationsAvailable) {
      displaySendInvoiceDocumentationQueryDialog(validateSellerTagAndSendInvoice);
    } else {
      validateSellerTagAndSendInvoice();
    }
  };

  const validateSellerTagAndSendInvoice = async () => {
    if (!sellerDimensionType && project?.hasSellerDimensionInInvoice) {
      dispatch(closeDialog());
      setTimeout(() => {
        dispatch(displayDialog(proceedWithSellerDimensionDeactivatedDialog));
      }, 300);
    } else {
      sendInvoiceDrafts();
    }
  };

  const sendInvoiceDrafts = async () => {
    setCreateInvoiceDraftsButtonSubmitting(true);
    timeReportingProjectService
      .createInvoiceDrafts(id, invoiceDocumentation!.data!)
      .then((invoiceIds) => {
        if (!invoiceIds?.data?.length) {
          displayNoInvoiceDraftsCreated();
        } else if (invoiceIds.data.length === 1) {
          displayOneInvoiceDraftSuccessfullyCreated();
          navigate(UrlHelper.formatPath(`/Invoice/${invoiceIds.data[0]}`), {
            state: { background: location },
          });
        } else {
          displayMultipleInvoiceDraftSuccessfullyCreated(invoiceIds.data.length);
          navigate(UrlHelper.formatPath(`/Reports/Revenue/Invoices`));
        }
      })
      .finally(() => {
        setCreateInvoiceDraftsButtonSubmitting(false);
      });
  };

  const displayNoInvoiceDraftsCreated = () => {
    dispatch(
      displayErrorMessage(
        LocalizationHelperInstance.getFormattedMessage(TranslationKeys.TimeReportingProject.NoInvoiceDraftsCreated)
      )
    );
  };

  const displayOneInvoiceDraftSuccessfullyCreated = () => {
    dispatch(
      displaySuccessMessage(
        LocalizationHelperInstance.getFormattedMessage(
          TranslationKeys.TimeReportingProject.OneInvoiceDraftSuccessfullyCreated
        )
      )
    );
  };

  const displayMultipleInvoiceDraftSuccessfullyCreated = (invoiceCount: number) => {
    dispatch(
      displaySuccessMessage(
        LocalizationHelperInstance.getFormattedMessage(
          TranslationKeys.TimeReportingProject.MultipleInvoiceDraftSuccessfullyCreated,
          "",
          { invoiceDraftCount: invoiceCount.toString() }
        )
      )
    );
  };

  const displaySendInvoiceDocumentationQueryDialog = (onSubmitSuccess?: () => Promise<void>) => {
    dispatch(displayDialog(getSendInvoiceDocumentationQueryDialog(onSubmitSuccess)));
  };

  const displayInvoiceConnectedSuccessfully = () => {
    dispatch(
      displaySuccessMessage(
        LocalizationHelperInstance.getFormattedMessage(
          TranslationKeys.TimeReportingProject.InvoiceConnectedSuccessfully
        )
      )
    );
  };

  const proceedWithSellerDimensionDeactivatedDialog: DialogProps = {
    isOpen: false,
    title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.TimeReportingProject.ProceedWithoutSellerTag),
    dialogType: DialogEnums.DialogType.Message,
    titleIcon: IconDefinitionConstants.WQuestionCircle,
    children: LocalizationHelperInstance.getFormattedMessage(
      TranslationKeys.TimeReportingProject.ProceedWithoutSellerTagDescription
    ),
    maxWidth: "sm",
    actions: [
      {
        text: "OK",
        onClick: sendInvoiceDrafts,
      },
    ],
  };

  const getConnectInvoiceMemberCategories = () => {
    return (invoiceDocumentation?.data?.personCategoryTimes ?? []).map((pct) => {
      const memberCategory: TimeReportingProjectFormTypes.ConnectInvoiceMemberCategory = {
        categoryId: pct.categoryId,
        memberId: pct.personId,
        categoryName: project?.projectCategories?.find((pc) => pc.id === pct.categoryId)?.name,
        memberName: project?.projectMembers?.find((pm) => pm.id === pct.personId)?.name,
      };
      return memberCategory;
    });
  };

  const onConnectInvoiceSuccess = (invoiceId: number) => {
    displayInvoiceConnectedSuccessfully();
    setLoading(true);
    loadData();
  };

  const connectInvoicesDialog: DialogProps = {
    isOpen: false,
    title: "",
    dialogType: DialogEnums.DialogType.Wizard,
    fullWidth: true,
    className: cssExports.connectInvoice,
    previewElementPlacement: "left",
    children: (
      <ConnectInvoiceFormContainer
        memberCategories={getConnectInvoiceMemberCategories()}
        projectId={project?.id}
        invoiceDocumentation={invoiceDocumentation?.data}
        onConnectSubmitSuccess={onConnectInvoiceSuccess}
        project={project}
      ></ConnectInvoiceFormContainer>
    ),
    maxWidth: "sm",
  };

  return loading ? (
    <LoadingWithLogo />
  ) : (
    <ReloadEvent
      render={(reloadKey, reload) => (
        <SearchContextProvider>
          {
            <PageLayouts.DashboardLayout<TimeReportingFormTypes.Filter>
              entityKey={id}
              title={project?.name ? project?.name : ""}
              topRightButtons={
                project && (
                  <CreateProfileButtons
                    project={project}
                    projectHasUnInvoicedProjectTime={projectHasUnInvoicedProjectTime}
                    onCreateInvoiceDrafts={processInvoiceDrafts}
                    createInvoiceDraftsButtonEnabled={createInvoiceDraftsButtonEnabled}
                    createInvoiceDraftsButtonSubmitting={createInvoiceDraftsButtonSubmitting}
                    connectInvoiceDialog={connectInvoicesDialog}
                  />
                )
              }
              hasPermissions={
                timeReportingProjectService.hasEditPermissions() || timeReportingProjectService.hasReadOnlyPermissions()
              }
              breadCrumb={<RouteBreadCrumb className={cssExports.dashBoardNavigation} current={project?.name ?? ""} />}
              filterFields={filterFormFields}
              filterQueryStateHandler={filterQueryStateHandler}
            >
              <TimeReportingProjectListViewContainer
                projectId={id}
                onInvoiceDocumentationUpdate={setInvoiceDocumentation}
                reloadKey={reloadKey}
              />
            </PageLayouts.DashboardLayout>
          }
        </SearchContextProvider>
      )}
    />
  );
};

export default TimeReportingProjectProfile;
